import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { MapPin, Phone, Mail, Printer, Clock, ChevronRight, Home } from 'lucide-react'
import { LocalizedLink } from '@/components/LocalizedLink'

export default function Contact() {
  const { t, i18n } = useTranslation('common')
  const [submitted, setSubmitted] = useState(false)
  const [error, setError] = useState(false)
  const [gdpr, setGdpr] = useState(false)

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!gdpr) {
      setError(true)
      return
    }
    setError(false)
    setSubmitted(true)
  }

  const padovaPhone = t('contact.details.padova.phone')
  const padovaFax = t('contact.details.padova.fax')
  const phoneNumber = padovaPhone.replace(/Tel:\s*/g, '').replace(/\s/g, '')

  return (
    <>
      <Helmet>
        <title>{t('contact.seo.title')}</title>
        <meta name="description" content={t('contact.seo.description')} />
        <html lang={i18n.language} />
      </Helmet>

      {/* Hero */}
      <section className="bg-brand-dark py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-white/60 mb-6">
            <LocalizedLink to="/" className="hover:text-white flex items-center gap-1 transition-colors">
              <Home size={14} />
              <span>Home</span>
            </LocalizedLink>
            <ChevronRight size={14} />
            <span className="text-white/90">{t('nav.contacts')}</span>
          </nav>
          <h1 className="text-3xl md:text-5xl font-bold text-white uppercase tracking-tight">
            {t('contact.hero.title')}
          </h1>
          <p className="mt-4 text-lg text-white/80 max-w-2xl">
            {t('contact.hero.subtitle')}
          </p>
        </div>
      </section>

      {/* Scrivici / Form */}
      <section className="py-16 md:py-24 bg-[#F9F6F1]">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-4xl font-bold text-brand-dark mb-4">
              {t('contact.form.writeUs')}
            </h2>
            <p className="text-brand-text text-lg">
              {t('contact.form.writeUsSubtitle')}
            </p>
          </div>

          {submitted ? (
            <div className="bg-green-50 border border-green-200 text-green-800 p-8 rounded-lg text-center">
              <p className="font-semibold">{t('contact.form.success')}</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-brand-dark mb-2">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    required
                    className="w-full px-4 py-3 border border-gray-200 bg-white text-brand-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-brand-dark mb-2">
                    {t('contact.form.company')}
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    className="w-full px-4 py-3 border border-gray-200 bg-white text-brand-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-brand-dark mb-2">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 border border-gray-200 bg-white text-brand-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors"
                  />
                </div>
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-brand-dark mb-2">
                    {t('contact.form.phone')}
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    className="w-full px-4 py-3 border border-gray-200 bg-white text-brand-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="subject" className="block text-sm font-medium text-brand-dark mb-2">
                  {t('contact.form.subject')}
                </label>
                <input
                  type="text"
                  id="subject"
                  name="subject"
                  className="w-full px-4 py-3 border border-gray-200 bg-white text-brand-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors"
                />
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-brand-dark mb-2">
                  {t('contact.form.message')}
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={5}
                  required
                  className="w-full px-4 py-3 border border-gray-200 bg-white text-brand-dark rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-red/20 focus:border-brand-red transition-colors resize-none"
                />
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="gdpr"
                  name="gdpr"
                  checked={gdpr}
                  onChange={(e) => setGdpr(e.target.checked)}
                  className="mt-1 h-4 w-4 rounded border-gray-300 text-brand-red focus:ring-brand-red"
                />
                <label htmlFor="gdpr" className="text-sm text-brand-text">
                  {t('contact.form.gdpr')}
                </label>
              </div>

              {error && (
                <p className="text-brand-red text-sm">
                  {t('contact.form.gdprError')}
                </p>
              )}

              <div className="flex justify-center pt-2">
                <button
                  type="submit"
                  className="inline-flex items-center justify-center bg-brand-red text-white px-12 py-3 rounded-lg font-medium hover:bg-brand-red-dark transition-colors"
                >
                  {t('contact.form.sendMessage')}
                </button>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Informazioni di contatto */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-2xl md:text-4xl font-bold text-brand-dark mb-12">
            {t('contact.info.heading')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Indirizzo */}
            <div className="flex items-start gap-4 p-6 rounded-xl border border-gray-100 bg-[#FAFAFA]">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center">
                <MapPin className="w-5 h-5 text-brand-red" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">
                  {t('contact.info.address.title')}
                </h3>
                <p className="text-brand-text leading-relaxed">
                  {t('contact.details.padova.address')}
                  <br />
                  {t('contact.details.padova.city')}
                </p>
                <p className="text-brand-text text-sm mt-2">
                  {t('contact.details.lucca.address')}, {t('contact.details.lucca.city')}
                </p>
                <p className="text-brand-text text-sm">
                  {t('contact.details.bari.address')}, {t('contact.details.bari.city')}
                </p>
              </div>
            </div>

            {/* Telefono */}
            <div className="flex items-start gap-4 p-6 rounded-xl border border-gray-100 bg-[#FAFAFA]">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center">
                <Phone className="w-5 h-5 text-brand-red" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">
                  {t('contact.info.phone.title')}
                </h3>
                <a
                  href={`tel:${phoneNumber}`}
                  className="text-brand-text hover:text-brand-red transition-colors"
                >
                  {padovaPhone.replace('Tel: ', '')}
                </a>
              </div>
            </div>

            {/* Email */}
            <div className="flex items-start gap-4 p-6 rounded-xl border border-gray-100 bg-[#FAFAFA]">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-brand-red" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">
                  {t('contact.info.email.title')}
                </h3>
                <a
                  href="mailto:info@chiggiatospa.com"
                  className="text-brand-text hover:text-brand-red transition-colors"
                >
                  info@chiggiatospa.com
                </a>
              </div>
            </div>

            {/* Fax */}
            <div className="flex items-start gap-4 p-6 rounded-xl border border-gray-100 bg-[#FAFAFA]">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center">
                <Printer className="w-5 h-5 text-brand-red" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">
                  {t('contact.info.fax.title')}
                </h3>
                <p className="text-brand-text">
                  {padovaFax.replace('Fax: ', '')}
                </p>
              </div>
            </div>

            {/* Orari */}
            <div className="flex items-start gap-4 p-6 rounded-xl border border-gray-100 bg-[#FAFAFA]">
              <div className="flex-shrink-0 w-12 h-12 rounded-full bg-brand-red/10 flex items-center justify-center">
                <Clock className="w-5 h-5 text-brand-red" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-brand-dark mb-2">
                  {t('contact.info.hours.title')}
                </h3>
                <p className="text-brand-text">
                  {t('contact.info.hours.value')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mappa */}
      <section className="w-full h-[400px] md:h-[500px]">
        <iframe
          src="https://www.google.com/maps?q=Via+dell'Industria,+15,+35020+Albignasego+PD&output=embed"
          width="100%"
          height="100%"
          style={{ border: 0 }}
          allowFullScreen
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          title={t('contact.info.address.title')}
        />
      </section>
    </>
  )
}
